import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { TaskCreate } from "@common/contracts/task.contract";
import { PersistableView, View } from "@common/types/views/views";
import {
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import { SelectedTransaction } from "@components/transactions/TransactionsBulkActions/transactionsBulkActionsContext";
import { TransactionsBulkActionsContextProvider } from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import {
    FiltersChangeFn,
    TransactionsFilters,
} from "@components/transactions/filters/lib";
import { TransactionsView } from "@components/transactions/views/TransactionsView";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import styles from "./CreateTaskDrawer.module.scss";
import { TaskCreationEntityFilter } from "./TaskCreationEntityFilter";
import {
    getTransactionsPreviewFilters,
    keepTaskSelectionOptions,
} from "./taskCreationPreview";

interface TaskCreationTransactionsPreviewProps {
    viewConfig: PersistableView;
    transactions?: SelectedTransaction[];
    isSelection?: boolean;
}

const renderEmptyPreview = () => <span>No transactions match this task</span>;

const useAvailableFilters = (): FilterTabs[] => {
    const isClassesEnabled = useIsClassesEnabled();
    const { enabled: isGlFirst } = useIsGlFirst();

    return useMemo(
        () => [
            FilterTabs.DATE,
            ...(isGlFirst ? [] : [FilterTabs.CATEGORY]),
            FilterTabs.ACCOUNT,
            ...(isClassesEnabled ? [FilterTabs.CLASS] : []),
            FilterTabs.AMOUNT,
            FilterTabs.COUNTERPARTY,
            FilterTabs.CUSTOMER,
            FilterTabs.NOTE,
            FilterTabs.RECEIPTS,
            FilterTabs.SPLIT,
            FilterTabs.RECONCILIATION,
            FilterTabs.MATCHED,
            FilterTabs.LEDGER,
            FilterTabs.TRANSACTION_LOCKED,
            FilterTabs.STATUS,
            FilterTabs.REVIEW,
        ],
        [isClassesEnabled, isGlFirst],
    );
};

interface PreviewBodyProps {
    defaultView: TransactionView;
    isSelection: boolean;
}

const PreviewBody = ({ defaultView, isSelection }: PreviewBodyProps) => {
    const formik = useFormikContext<TaskCreate>();
    const { currentView, transactionCount } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters, setCurrentView, setPage } = useContext(
        PaginatedTransactionsActionsContext,
    );
    const transactionsViewConfig = useTransactionViewConfig();
    const availableFilters = useAvailableFilters();

    const { values, setFieldValue } = formik;
    const taskView = values.view;

    const applyPreviewFilters = useCallback(
        (value: Partial<TransactionsFilters>) => {
            const merged = omitBy(
                { ...currentView.filters, ...value },
                (filterValue) =>
                    filterValue === null || filterValue === undefined,
            );
            if (isEqual(merged, currentView.filters)) {
                return undefined;
            }
            setPage(1);
            setCurrentFilters(merged);
            return merged;
        },
        [currentView.filters, setCurrentFilters, setPage],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            if (!taskView) {
                return;
            }
            const merged = applyPreviewFilters(value);
            if (merged) {
                void setFieldValue("view", { ...taskView, filters: merged });
            }
        },
        [applyPreviewFilters, setFieldValue, taskView],
    );

    const formEntityIds = values.entityIds?.length
        ? values.entityIds
        : undefined;
    useEffect(() => {
        if (isSelection) {
            return;
        }
        applyPreviewFilters({ entityIds: formEntityIds });
    }, [isSelection, formEntityIds, applyPreviewFilters]);

    const handleSearchChange = useCallback(
        (search: string) => handleFiltersChange({ search }),
        [handleFiltersChange],
    );

    const syncTaskColumns = useCallback(
        (next: View) => {
            if (!taskView) {
                return;
            }
            void setFieldValue("view", {
                ...taskView,
                columns: next.columns,
                options: keepTaskSelectionOptions({
                    previewOptions: next.options,
                    taskOptions: taskView.options,
                }),
            });
        },
        [setFieldValue, taskView],
    );

    const handleColumnSettingsChange = useCallback(
        (next: TransactionView) => {
            setCurrentView((previous) => ({
                ...previous,
                columns: next.columns,
                options: next.options,
            }));
            syncTaskColumns(next);
        },
        [setCurrentView, syncTaskColumns],
    );

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={currentView.filters}
            totalTransactions={transactionCount}
            onUpdated={() => {}}
        >
            <TablePageHeader
                isFullPage
                filters={currentView.filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                search={currentView.filters.search ?? ""}
                onSearchChange={handleSearchChange}
                searchPlaceholder="Search transactions…"
                ledgerFilterLabel="Cash ledger"
                hideClearButton
            >
                {!isSelection && <TaskCreationEntityFilter />}
                <ColumnSettings<TransactionView>
                    view={currentView}
                    viewConfig={transactionsViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleColumnSettingsChange}
                />
            </TablePageHeader>
            <TransactionsView
                view={currentView}
                emptyView={renderEmptyPreview}
                onViewChange={syncTaskColumns}
                fixedTopOffsetPx
            />
        </TransactionsBulkActionsContextProvider>
    );
};

const parsePreviewView = ({
    taskView,
    transactions,
}: {
    taskView: PersistableView;
    transactions?: SelectedTransaction[];
}): TransactionView | undefined => {
    const parsed = TransactionViewSchema.safeParse({
        ...taskView,
        filters: getTransactionsPreviewFilters({
            filters: taskView.filters,
            transactions,
        }),
        options: {
            ...taskView.options,
            selection: false,
            bulkActions: false,
        },
    });
    return parsed.success ? parsed.data : undefined;
};

export const TaskCreationTransactionsPreview = ({
    viewConfig,
    transactions,
    isSelection = false,
}: TaskCreationTransactionsPreviewProps) => {
    const [page, setPage] = useState(1);
    const defaultView = useMemo(
        () => parsePreviewView({ taskView: viewConfig, transactions }),
        [viewConfig, transactions],
    );

    if (!defaultView) {
        return null;
    }

    return (
        <div className={styles.previewPane}>
            <div className={styles.previewSurface}>
                <PaginatedTransactionsTableContextProvider
                    defaultView={defaultView}
                    paginationMode={{ type: "pages", page, setPage }}
                    disableSimilarTransactionsUpdate
                >
                    <PreviewBody
                        defaultView={defaultView}
                        isSelection={isSelection}
                    />
                </PaginatedTransactionsTableContextProvider>
            </div>
        </div>
    );
};
